import type { OnlineUser } from '../store/online-store';

/**
 * Una riga della lista dei collegati: un giocatore, o i DUE che si stanno
 * affrontando.
 */
export type RigaOnline =
  | { kind: 'single'; user: OnlineUser }
  | { kind: 'pair'; a: OnlineUser; b: OnlineUser };

/**
 * Una partita = una riga.
 *
 * Due collegati che giocano l'uno contro l'altro occupavano due righe gemelle
 * — «Ada · Gioca vs Bo» e «Bo · Gioca vs Ada» — ciascuna col suo «Guarda» che
 * portava alla stessa scacchiera. In una lista alta tre righe il doppione ne
 * mangiava due per una partita sola.
 *
 * ## L'accoppiamento e' per NOME, e RECIPROCO
 *
 * Il nome e' l'unica chiave disponibile: `opponent` porta il nome e non lo
 * `userId`, perche' lo riempiono DUE strade — la risposta a `online:list` e
 * l'annuncio `online:update` — e la seconda non puo' portare altro. In DB il
 * nome e' unico e senza distinzione di maiuscole (migrazione 019), quindi il
 * confronto si fa in minuscolo su tutti e due i lati, come fa gia'
 * `tavolo-aperto.ts` per unire la lista dei tavoli a quella dei collegati.
 *
 * Reciproco, e non e' prudenza di troppo: si fondono solo due righe che si
 * nominano a vicenda e che stanno TUTTE E DUE in `playing`. Una nomina a una
 * voce sola — l'altra riga non e' in lista (sta giocando con chi guarda, che
 * dalla lista e' escluso), o il suo annuncio e' arrivato a meta' — ricade su
 * due righe singole, cioe' sul comportamento di prima. E' anche cio' che
 * distingue il triangolo: con `A→B`, `B→A` e `C→B` si fonde `A–B` e `C` resta
 * da solo, invece di sparire dentro una coppia che non esiste.
 *
 * ## L'ordine
 *
 * Si passa sull'array GIA' ORDINATO e la coppia si posa dove stava il primo
 * dei due membri, che diventa `a`. Conseguenza dichiarata: quando i due stanno
 * nei due gruppi dell'ordinamento — registrati prima, ospiti dopo — la coppia
 * scavalca quella chiave, e il caso e' il piu' frequente di tutti (un umano
 * contro un bot, che sul filo e' un ospite). In pratica si sposta poco: un
 * registrato in partita e' l'ULTIMO del blocco dei registrati (`rangoGiocabilita`
 * lo mette a 4) e il bot suo compagno finiva comunque in fondo, quindi la riga
 * unita si posa esattamente sul confine fra i due blocchi.
 *
 * Funzione pura, nessuna copia degli utenti: le righe sono gli stessi oggetti.
 */
export function righeOnline(users: OnlineUser[]): RigaOnline[] {
  /* Si costruisce sempre, anche senza nessuno in partita: una passata in piu'
     su una lista di qualche decina di voci, contro un ramo in meno da leggere. */
  const perNome = new Map<string, OnlineUser>();
  for (const u of users) perNome.set(u.username.toLowerCase(), u);

  const consumati = new Set<string>();
  const righe: RigaOnline[] = [];

  for (const a of users) {
    if (consumati.has(a.userId)) continue;
    const b = compagno(a, perNome);
    if (b && !consumati.has(b.userId)) {
      consumati.add(a.userId);
      consumati.add(b.userId);
      righe.push({ kind: 'pair', a, b });
      continue;
    }
    righe.push({ kind: 'single', user: a });
  }

  return righe;
}

/** Chi sta giocando con `a`, se e' in lista e lo nomina a sua volta. */
function compagno(a: OnlineUser, perNome: Map<string, OnlineUser>): OnlineUser | undefined {
  if (a.status !== 'playing' || !a.opponent) return undefined;
  const b = perNome.get(a.opponent.toLowerCase());
  /* `b.userId !== a.userId`: una riga che nominasse se stessa non si accoppia
     con se stessa. Non dovrebbe capitare, ma qui costa un confronto. */
  if (!b || b.userId === a.userId || b.status !== 'playing') return undefined;
  return b.opponent?.toLowerCase() === a.username.toLowerCase() ? b : undefined;
}
